import { DOMNode } from 'html-react-parser';

import { TestFunction } from '../HTMLShadowDomRenderer.types';

const siblingIndexCache = new WeakMap<DOMNode[], Map<DOMNode, number>>();
const firstMatchIndexCache = new WeakMap<
  DOMNode[],
  Map<TestFunction, number>
>();
const chainHeadCache = new WeakMap<DOMNode, DOMNode>();
const chainSiblingsCache = new WeakMap<DOMNode, DOMNode[]>();

/**
 * Returns the first node in domNode's previousSibling/nextSibling chain,
 * memoized per node.
 *
 * Iterative (not recursive) on purpose: a message with a huge number of
 * parentless siblings would otherwise make the first, uncached lookup on
 * the last node in the chain recurse once per sibling, overflowing the call
 * stack (confirmed - a naive recursive version crashes at 10k+ siblings).
 */
function getChainHead(domNode: DOMNode): DOMNode {
  const path: DOMNode[] = [];
  let current = domNode;

  while (!chainHeadCache.has(current) && current.previousSibling) {
    path.push(current);
    current = current.previousSibling;
  }

  const head = chainHeadCache.get(current) ?? current;

  for (const node of path) {
    chainHeadCache.set(node, head);
  }

  chainHeadCache.set(domNode, head);

  return head;
}

/**
 * Returns the array of nodes domNode belongs to: parent.children when a
 * parent exists, otherwise the previousSibling/nextSibling chain built from
 * its (memoized) chain head - top-level fragment nodes have no parent but
 * still link to each other via previousSibling/nextSibling.
 */
function getSiblingsGroup(domNode: DOMNode): DOMNode[] {
  if (domNode.parent) {
    return domNode.parent.children;
  }

  const head = getChainHead(domNode);
  let siblings = chainSiblingsCache.get(head);

  if (!siblings) {
    siblings = [];

    for (let node: DOMNode | null = head; node; node = node.nextSibling) {
      siblings.push(node);
    }

    chainSiblingsCache.set(head, siblings);
  }

  return siblings;
}

/** Returns the index of a node within a given siblings array, memoized per array. */
function getSiblingIndex(domNode: DOMNode, siblings: DOMNode[]): number {
  let indexMap = siblingIndexCache.get(siblings);

  if (!indexMap) {
    indexMap = new Map();
    siblings.forEach((sibling, index) => indexMap!.set(sibling, index));
    siblingIndexCache.set(siblings, indexMap);
  }

  return indexMap.get(domNode) ?? -1;
}

/** Returns the index of the first sibling passing test, memoized per array/test pair. */
function getFirstMatchIndex(siblings: DOMNode[], test: TestFunction): number {
  let testMap = firstMatchIndexCache.get(siblings);

  if (!testMap) {
    testMap = new Map();
    firstMatchIndexCache.set(siblings, testMap);
  }

  let index = testMap.get(test);

  if (index === undefined) {
    index = siblings.findIndex(test);
    testMap.set(test, index);
  }

  return index;
}

/**
 * Checks if a node has a previous sibling passing the given test function.
 *
 * Equivalent to walking domNode.previousSibling backwards, but memoized per
 * sibling group so a wide/flat list of N siblings costs O(N) total instead of
 * O(N^2) - a node with many siblings would otherwise make a naive walk very
 * slow. Works the same whether or not domNode has a DOM parent.
 */
export function hasSomePreviousSiblingPassingTest(
  domNode: DOMNode,
  test: TestFunction,
): boolean {
  const siblings = getSiblingsGroup(domNode);
  const matchIndex = getFirstMatchIndex(siblings, test);

  if (matchIndex === -1) {
    return false;
  }

  return matchIndex < getSiblingIndex(domNode, siblings);
}
